<template>
  <v-app full-height>
    <v-main>
      <router-view/>
    </v-main>

    <app-alert />

    <!-- Global Video Call Modal - Available from anywhere in the app -->
    <IncomingCallModal
      v-model:visible="globalVideoCallState.incomingCall.visible"
      :call-data="globalVideoCallState.incomingCall.data"
      :caller="globalVideoCallState.incomingCall.caller"
      :call-type="globalVideoCallState.incomingCall.type"
      :room-id="globalVideoCallState.incomingCall.roomId"
      :conversation-id="getConversationIdFromCall()"
      :expires-at="globalVideoCallState.incomingCall.expiresAt"
      :call-id="globalVideoCallState.incomingCall.callId"
      @call-accepted="handleGlobalCallAccepted"
      @call-declined="handleGlobalCallDeclined"
      @call-expired="handleGlobalCallExpired"
    />

    <!-- Global Video Call Interface - Available from anywhere in the app -->
    <VideoCallInterface
      v-if="globalVideoCallState.activeCall.active && !globalVideoCallState.activeCall.minimized"
      :room-url="globalVideoCallState.activeCall.roomUrl"
      :room-id="globalVideoCallState.activeCall.roomId"
      :call-type="globalVideoCallState.activeCall.type"
      :call-id="globalVideoCallState.activeCall.callId"
      :user-role="globalVideoCallState.activeCall.userRole"
      :start-time="globalVideoCallState.activeCall.startTime"
      @call-ended="handleGlobalCallEnded"
      @call-minimized="handleGlobalCallMinimized"
    />

    <!-- Global Call Status Indicator - Available from anywhere in the app -->
    <CallStatusIndicator
      v-if="globalVideoCallState.activeCall.active"
      :call-type="globalVideoCallState.activeCall.type"
      :room-id="globalVideoCallState.activeCall.roomId"
      :call-id="globalVideoCallState.activeCall.callId"
      :minimized="globalVideoCallState.activeCall.minimized"
      :start-time="globalVideoCallState.activeCall.startTime"
      @restore-call="handleGlobalCallRestored"
      @end-call="handleGlobalCallEnded"
    />
  </v-app>
</template>

<script>
import AppAlert from './components/AppAlert.vue';
import IncomingCallModal from './components/video-calls/IncomingCallModal.vue';
import VideoCallInterface from './components/video-calls/VideoCallInterface.vue';
import CallStatusIndicator from './components/video-calls/CallStatusIndicator.vue';
import globalVideoCallService from './services/video-calls/global';
import { saveInLocalStorage } from './utils/local-storage';
import { reactive, onMounted, onUnmounted } from 'vue';

export default {
  components: { 
    AppAlert,
    IncomingCallModal,
    VideoCallInterface,
    CallStatusIndicator
  },

  name: 'App',

  setup() {
    // Reactive state for global video calls
    const globalVideoCallState = reactive({
      incomingCall: {
        visible: false,
        data: null,
        caller: null,
        type: 'video',
        roomId: null,
        expiresAt: null,
        callId: null
      },
      activeCall: {
        active: false,
        minimized: false,
        type: 'video',
        roomId: null,
        roomUrl: null,
        startTime: null,
        callId: null,
        userRole: 'participant'
      }
    });

    // Initialize global video call service
    onMounted(() => {
      console.log('🚀 INITIALIZING APP WITH GLOBAL VIDEO CALL SUPPORT');
      
      // Initialize the global video call service
      globalVideoCallService.initialize();
      
      // Set up global video call event listeners
      setupGlobalVideoCallListeners();
      
      console.log('✅ APP INITIALIZATION COMPLETE WITH GLOBAL VIDEO CALL SUPPORT');
    });

    // Clean up when app is unmounted
    onUnmounted(() => {
      console.log('🧹 CLEANING UP APP GLOBAL VIDEO CALL SUPPORT');
      globalVideoCallService.cleanup();
    });

    // Set up global video call event listeners
    const setupGlobalVideoCallListeners = () => {
      // Listen for incoming call events
      globalVideoCallService.on('incoming-call-received', (incomingCall) => {
        console.log('📞 GLOBAL INCOMING CALL EVENT RECEIVED IN APP:', incomingCall);
        console.log('👤 CALLER DATA IN APP:', incomingCall.caller);
        Object.assign(globalVideoCallState.incomingCall, incomingCall);
        console.log('📞 FINAL STATE AFTER ASSIGNMENT:', globalVideoCallState.incomingCall);
      });

      // Listen for call started events
      globalVideoCallService.on('call-started', (activeCall) => {
        console.log('🎥 GLOBAL CALL STARTED EVENT RECEIVED IN APP:', activeCall);
        Object.assign(globalVideoCallState.activeCall, activeCall);
      });

      // Listen for call ended events
      globalVideoCallService.on('call-ended', () => {
        console.log('🔚 GLOBAL CALL ENDED EVENT RECEIVED IN APP');
        globalVideoCallState.activeCall = {
          active: false,
          minimized: false,
          type: 'video',
          roomId: null,
          roomUrl: null,
          startTime: null,
          callId: null,
          userRole: 'participant'
        };
      });

      // Listen for call minimized events
      globalVideoCallService.on('call-minimized', (activeCall) => {
        console.log('📱 GLOBAL CALL MINIMIZED EVENT RECEIVED IN APP:', activeCall);
        globalVideoCallState.activeCall.minimized = true;
      });

      // Listen for call restored events
      globalVideoCallService.on('call-restored', (activeCall) => {
        console.log('🔄 GLOBAL CALL RESTORED EVENT RECEIVED IN APP:', activeCall);
        globalVideoCallState.activeCall.minimized = false;
      });
    };

    // Handle global call accepted
    const handleGlobalCallAccepted = (callData) => {
      console.log('✅ GLOBAL CALL ACCEPTED IN APP:', callData);
      globalVideoCallService.handleCallAccepted(callData);
    };

    // Handle global call declined
    const handleGlobalCallDeclined = (callData) => {
      console.log('❌ GLOBAL CALL DECLINED IN APP:', callData);
      globalVideoCallService.handleCallDeclined(callData);
    };

    // Handle global call expired
    const handleGlobalCallExpired = (callData) => {
      console.log('⏰ GLOBAL CALL EXPIRED IN APP:', callData);
      globalVideoCallService.handleCallExpired(callData);
    };

    // Handle global call ended
    const handleGlobalCallEnded = () => {
      console.log('🔚 GLOBAL CALL ENDED IN APP');
      globalVideoCallService.endActiveCall();
    };

    // Handle global call minimized
    const handleGlobalCallMinimized = () => {
      console.log('📱 GLOBAL CALL MINIMIZED IN APP');
      globalVideoCallService.minimizeCall();
    };

    // Handle global call restored
    const handleGlobalCallRestored = () => {
      console.log('🔄 GLOBAL CALL RESTORED IN APP');
      globalVideoCallService.restoreCall();
    };

    // Get conversation ID from call data for the modal
    const getConversationIdFromCall = () => {
      return globalVideoCallState.incomingCall.data?.conversation_id || 'global';
    };

    return {
      globalVideoCallState,
      handleGlobalCallAccepted,
      handleGlobalCallDeclined,
      handleGlobalCallExpired,
      handleGlobalCallEnded,
      handleGlobalCallMinimized,
      handleGlobalCallRestored,
      getConversationIdFromCall
    };
  },

  watch: {
    '$i18n.locale': function(v) {
      document.querySelector('html')
      .setAttribute('dir', v == 'ar' ? 'rtl' : 'ltr');
      saveInLocalStorage('language', v);
    }
  },
  
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@200;300;400;500;600;700;800;900;1000&display=swap');
/* @import url('https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap'); */
</style>